import { useCallback, useState } from 'react';
import { useAuth } from '@/contexts/customer-auth-context';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import {
  nextAfterOffer,
  type OfferFrequency,
  type OfferOutcome,
  type PaymentOfferDto,
  submitPaymentOfferOnConnect,
} from '@/lib/connect-api';
import { toSubunits } from '@/lib/currency';
import { toScheduleDate } from '@/lib/payment-schedule';
import { planDeepLink } from '@/lib/redirect';
import { useTranslation } from '@/hooks/useTranslation';

/** The plan the customer configured, as the slider left it. */
export interface OfferTerms {
  frequency: OfferFrequency;
  count: number;
  startDate: Date;
  /** The figure the schedule divides — discounted where a live discount applies. */
  totalAmount: number;
}

/**
 * Whether two plans are the same proposal, so a verdict taken on one stands for the other.
 *
 * The start date is compared by calendar day, not by instant: a plan configured in the tab
 * carries a mount-time `new Date()` with a time of day, while one re-hydrated from a
 * `yyyy-MM-dd` deep-link string parses to local midnight. An instant comparison would never
 * match and every edit would resubmit.
 */
export function sameOfferTerms(a: OfferTerms, b: OfferTerms): boolean {
  return (
    a.frequency === b.frequency &&
    a.count === b.count &&
    a.totalAmount === b.totalAmount &&
    toScheduleDate(a.startDate) === toScheduleDate(b.startDate)
  );
}

/**
 * A verdict the customer has to read. Never `accepted` — an accepted offer goes straight on to
 * review or setup, so it is returned as `{ go: 'checkout' }` rather than held.
 */
export type ReferredOutcome = Exclude<OfferOutcome, 'accepted'>;

export interface HeldOutcome {
  outcome: ReferredOutcome;
  offer: PaymentOfferDto | null;
  /**
   * Frozen at submit. The tab's clamp effect can still move `paymentCount` while a
   * confirmation is on screen — `DebtDataContext` schedules a refresh for the moment a
   * discount expires — so the CTA must build its schedule from what was decided.
   */
  terms: OfferTerms;
}

export type OfferGateResult = { go: 'checkout' } | { go: 'assessment'; url: string } | { go: 'stop' };

export interface UseOfferGate {
  submit: (terms: OfferTerms) => Promise<OfferGateResult>;
  outcome: HeldOutcome | null;
  error: string | null;
  reset: () => void;
}

interface UseOfferGateOptions {
  /** The case being offered on, or null for an offer covering the whole account. */
  caseId: string | null;
  /** Where to send the customer when an assessment is required, and where to bring them back to. */
  basePath: string;
}

/**
 * Submits the configured plan as a payment offer and says what to do with the verdict.
 *
 * Fails closed. This is a control: an offer the engine would have referred must not become a
 * live plan because the call happened to fail, so every error stops the flow rather than
 * falling through to checkout. The one exception is preview/demo mode, which carries no
 * credential and never reaches a real customer.
 *
 * There is no loading flag here on purpose. The caller has to hold its own CTA disabled across
 * the whole submit-then-checkout sequence anyway — a flag of ours would either duplicate that
 * or, if left set on the checkout hand-off, strand the button when checkout itself failed.
 */
export function useOfferGate({ caseId, basePath }: UseOfferGateOptions): UseOfferGate {
  const { authStage, getAccessToken } = useAuth();
  const { useMockData, isPreviewMode } = useConfiguration();
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<HeldOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setOutcome(null);
    setError(null);
  }, []);

  const submit = useCallback(
    async (terms: OfferTerms): Promise<OfferGateResult> => {
      if (useMockData || isPreviewMode) return { go: 'checkout' };

      setError(null);
      try {
        const response = await submitPaymentOfferOnConnect(
          {
            case_id: caseId,
            // Rounded up, matching the server's own monthly-equivalent rounding: understating
            // the instalment is the direction that makes an unaffordable plan look affordable.
            instalment_amount_cents: Math.ceil(toSubunits(terms.totalAmount) / terms.count),
            frequency: terms.frequency,
            instalment_count: terms.count,
          },
          authStage,
          getAccessToken,
        );

        const next = nextAfterOffer(response);
        if (next.next === 'assessment') {
          const back = planDeepLink({ ...terms, basePath, assessed: true });
          return { go: 'assessment', url: `/budget-assessment?tier=${next.tier}&redirect=${encodeURIComponent(back)}` };
        }
        // An accepted offer never pauses on a confirmation. The review screen is now the last
        // step before setup on every card path, so a screen saying "your plan is ready to set
        // up" would sit in front of the one that actually sets it up.
        if (next.outcome === 'accepted') return { go: 'checkout' };
        setOutcome({ outcome: next.outcome, offer: next.offer, terms });
        return { go: 'stop' };
      } catch {
        // One message for every refusal, and the error code deliberately not read. The codes look
        // like they separate transient from permanent and they do not: `INVALID_STATE` is the
        // router's pre-checks, `CONFLICT` is the same conditions re-checked inside the write
        // transaction, so "a plan already runs here" and "an account-wide offer is already in
        // review" arrive under either one depending on whether it landed in the intervening
        // moment. Promising a retry on `CONFLICT` would tell a customer whose account already
        // has a plan to keep trying something that can never succeed.
        setError(t('paymentOffer.cannotSubmit'));
        return { go: 'stop' };
      }
    },
    [authStage, basePath, caseId, getAccessToken, isPreviewMode, t, useMockData],
  );

  return { submit, outcome, error, reset };
}
